import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, Link2, Link2Off, Search } from 'lucide-react';
import { JiraLinkSource } from '../../types/auth';

export interface JiraPerson {
  account_id: string;
  display_name: string | null;
  email_address: string | null;
  active?: boolean;
  account_type?: string;
}

interface Props {
  /** The login this control belongs to, with the link it has now. */
  login: {
    id: number;
    username: string;
    jiraAccountId?: string | null;
    jiraDisplayName?: string | null;
    jiraLinkSource?: JiraLinkSource | null;
  };
  /** Every Jira person; the picker keeps real people only (no app accounts). */
  people: JiraPerson[];
  /** Jira account id -> username of the login that holds it. */
  holders: Map<string, string>;
  /** Saves the choice: an account id, or null to remove the link. Throws when it could not be saved. */
  onChange: (accountId: string | null) => Promise<void>;
}

const SHOWN = 40;

const initials = (name?: string | null) =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

const INPUT =
  'w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-500 ' +
  'shadow-sm transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100';

const PRESS = 'transition-[transform,background-color,color,box-shadow] duration-150 active:scale-[0.98] motion-reduce:transform-none';

// One line under a login: which Jira person it is tied to, and a small picker to change that.
// Opening the picker puts the cursor in the search box; Escape closes it and returns to the chip.
export default function JiraLinkControl({ login, people, holders, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const chipRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const linked = !!login.jiraAccountId;
  const name = login.jiraDisplayName || login.jiraAccountId || '';
  const how = login.jiraLinkSource === 'manual' ? 'by hand' : 'by email';

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people
      .filter((p) => (p.account_type ?? 'atlassian') === 'atlassian')
      .filter((p) => !q || (p.display_name ?? '').toLowerCase().includes(q) || (p.email_address ?? '').toLowerCase().includes(q))
      .sort((a, b) => Number(b.active !== false) - Number(a.active !== false) || (a.display_name ?? '').localeCompare(b.display_name ?? ''));
  }, [people, query]);
  const shown = matches.slice(0, SHOWN);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    setQuery('');
    if (returnFocus) chipRef.current?.focus();
  };

  const choose = async (accountId: string | null) => {
    if (busy) return;
    setBusy(true);
    try {
      await onChange(accountId);
      close(true);
    } catch {
      // The reason has been shown by the caller; keep the list open so another person can be picked.
    } finally {
      setBusy(false);
    }
  };

  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close(true);
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[data-person]:not([disabled])') ?? []);
    if (items.length === 0) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'ArrowDown') items[at + 1 < items.length ? at + 1 : 0].focus();
    else if (at <= 0) inputRef.current?.focus();
    else items[at - 1].focus();
  };

  return (
    <div className="border-t border-gray-100 px-3.5 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-gray-500">Jira person</span>
        <button
          ref={chipRef}
          type="button"
          onClick={() => (open ? close(false) : setOpen(true))}
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          aria-label={linked ? `Linked to ${name} ${how}. Change the Jira person for ${login.username}` : `Not linked to a Jira person. Link one for ${login.username}`}
          className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium ring-1 ring-inset ${PRESS} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
            linked ? 'bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100' : 'bg-amber-50 text-amber-900 ring-amber-200 hover:bg-amber-100'
          }`}
        >
          {linked ? <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : <Link2Off className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
          <span className="truncate">{linked ? name : 'Not linked'}</span>
          {linked && <span className="shrink-0 font-normal opacity-70">· {how}</span>}
        </button>
      </div>

      {open && (
        <div id={panelId} ref={panelRef} role="dialog" aria-label={`Pick a Jira person for ${login.username}`} onKeyDown={onPanelKeyDown} className="mt-2.5 rounded-xl border border-gray-200 bg-gray-50/70 p-2.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or email…"
              aria-label={`Search Jira people for ${login.username}`}
              className={INPUT}
            />
          </div>

          {people.length === 0 ? (
            <p className="py-5 text-center text-sm text-gray-500">No Jira people yet. Sync users from Jira first.</p>
          ) : shown.length === 0 ? (
            <p className="py-5 text-center text-sm text-gray-500">No Jira people match.</p>
          ) : (
            <ul className="mt-2 max-h-60 space-y-1 overflow-y-auto pr-0.5" aria-label="Jira people">
              {shown.map((p) => {
                const current = p.account_id === login.jiraAccountId;
                const takenBy = current ? undefined : holders.get(p.account_id);
                return (
                  <li key={p.account_id}>
                    <button
                      type="button"
                      data-person
                      disabled={busy || takenBy !== undefined}
                      onClick={() => void choose(p.account_id)}
                      aria-current={current || undefined}
                      className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left ${PRESS} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                        current
                          ? 'bg-emerald-50 ring-1 ring-emerald-200'
                          : takenBy !== undefined
                            ? 'cursor-not-allowed opacity-60'
                            : 'hover:bg-white hover:shadow-sm active:bg-gray-100'
                      }`}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-[0.6875rem] font-semibold text-gray-600">
                        {initials(p.display_name || p.email_address)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900">{p.display_name || p.email_address || p.account_id}</span>
                        {p.email_address && <span className="block truncate text-xs text-gray-500">{p.email_address}</span>}
                        {takenBy !== undefined && <span className="block truncate text-[0.6875rem] text-gray-500">Linked to @{takenBy}</span>}
                      </span>
                      {current ? (
                        <Check className="h-4 w-4 shrink-0 text-emerald-600" aria-label="Linked now" />
                      ) : takenBy === undefined && p.active === false ? (
                        <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[0.6875rem] font-medium text-gray-600">Inactive</span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="min-w-0 flex-1 text-xs text-gray-500" aria-live="polite">
              {matches.length > SHOWN ? `Showing ${SHOWN} of ${matches.length}. Type to narrow the list.` : `${matches.length} ${matches.length === 1 ? 'person' : 'people'}`}
            </p>
            {linked && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void choose(null)}
                className={`rounded-lg px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 ${PRESS} focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300`}
              >
                Remove link
              </button>
            )}
            <button
              type="button"
              onClick={() => close(true)}
              className={`rounded-lg bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-100 ${PRESS} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}
            >
              Close
            </button>
          </div>
          {login.jiraLinkSource === 'auto_email' && (
            <p className="mt-1.5 text-xs text-gray-500">Linked because the emails match. If you remove it, the next sign-in links it again; pick someone else to keep a different person.</p>
          )}
        </div>
      )}
    </div>
  );
}
